import { useVideoPlayer } from '@folklore/hooks';
import { useVideoTracking } from '@folklore/tracking';
import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';
import { RefObject, useEffect } from 'react';

import getVideoSources from '../../lib/getVideoSources';

import styles from '../../styles/partials/bump.module.css';

export interface BumpProps {
    brand: Brand;
    playing?: boolean;
    playTimeout?: number;
    onEnd?: (() => void) | null;
    onPlay?: (() => void) | null;
    onFail?: (() => void) | null;
    className?: string | null;
}

const Bump = ({
    brand,
    playing: shouldPlay = false,
    playTimeout = 500,
    onEnd = null,
    onPlay = null,
    onFail = null,
    className = null,
}: BumpProps) => {
    'use no memo';
    const { handle, videos: brandVideos = {}, label } = brand;
    const { bumperPortrait: defaultVideo = null } = brandVideos || {};
    const { name: defaultVideoName = null } = defaultVideo || {};
    const videoName =
        defaultVideoName !== null ? defaultVideoName.replace(/(Horizontal|Vertical)/, '') : null;
    const videoId = videoName !== null ? `${videoName}-${handle}` : null;
    const player = useVideoPlayer({
        videoId,
        url: videoId,
        muted: true,
        initialMuted: true,
        onEnd,
        onPlay,
    });
    const { ref: videoRef, playing = false, duration } = player;
    useVideoTracking(player, {
        id: videoId,
        platform: 'native',
        title: videoName !== null ? `${label} - ${videoName}` : null,
        duration,
    });

    // const onVideoSuspend = useCallback(
    //     (e) => {
    //         const video = e.currentTarget;
    //         const isVideoPlaying = !!(
    //             video.currentTime > 0 &&
    //             !video.paused &&
    //             !video.ended &&
    //             video.readyState > 2
    //         );
    //         if (!isVideoPlaying && onEnd !== null) {
    //             onEnd();
    //         }
    //     },
    //     [onEnd],
    // );

    useEffect(() => {
        const { current: video = null } = videoRef as RefObject<HTMLVideoElement>;
        const { currentTime = 0, paused = false, ended = false, readyState = 0 } = video || {};
        const isVideoPlaying =
            video !== null && !paused && !ended && (readyState > 2 || currentTime > 0.05);
        if (playing || !shouldPlay || isVideoPlaying) {
            return () => {};
        }
        const timeout = setTimeout(() => {
            if (onFail !== null) {
                onFail();
            }
        }, playTimeout);
        return () => {
            clearTimeout(timeout);
        };
    }, [handle, playing, shouldPlay, onFail, playTimeout]);

    return (
        <div
            className={classNames([
                styles.container,
                styles[videoName],
                styles[handle],
                {
                    [styles.playing]: playing,
                },
                className,
            ])}
        >
            <video
                key={`video-${handle}`}
                ref={videoRef as RefObject<HTMLVideoElement>}
                className={styles.video}
                preload="auto"
                muted
                playsInline
                autoPlay={shouldPlay}
                // crossOrigin="anonymous"
                // onSuspend={onVideoSuspend}
            >
                {['portrait', 'landscape', null].map((orientation) => {
                    const { sources = [] } =
                        brandVideos[camelCase(`bumper_${orientation || 'portrait'}`)] || {};
                    return getVideoSources(sources)
                        .filter(({ mime }) => (mime || '').indexOf('webm') === -1)
                        .map(({ mime, url }) => (
                            <source
                                key={`source-${orientation || 'default'}-${url}-${mime}`}
                                src={`${url}#t=0.001`}
                                type={mime}
                                media={
                                    orientation !== null ? `(orientation: ${orientation})` : null
                                }
                            />
                        ));
                })}
            </video>
        </div>
    );
};

export default Bump;
